import React, { useContext, useState } from 'react';
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { FaStar, FaBookmark, FaRegBookmark } from "react-icons/fa";
import Pagination from "../../components/common/Pagination";
import ServiceCard from "../../components/buyer/ServiceCard";
import ProductCard from "../../components/buyer/ProductCard";
import { AuthContext } from '../../utils/AuthContext';
import Reviews from '../../components/common/Reviews';
import { hostNameBack } from '../../utils/constants';
import AdminSendEmailPopup from '../../components/admin/AdminSendEmailPopup';
import Loader from '../../utils/Loader';
import ChatPopup from '../../components/buyer/ChatPopup';
import GenerateAccountStatement from '../../components/common/GenerateAccountStatement';
import { RiRadioButtonLine } from 'react-icons/ri';
import { formatDate } from '../../utils/utilFuncs';
import AvailabilityStatus from '../../components/buyer/AvailabilityStatus';

function Profile() {
    const { id } = useParams();
    const [seller, setSeller] = React.useState(null);
    const [services, setServices] = React.useState([]);
    const [products, setProducts] = React.useState([]);
    const [totalServicePages, setTotalServicePages] = React.useState(1);
    const [totalProductPages, setTotalProductPages] = React.useState(1);
    const [crrServicePage, setCrrServicePage] = React.useState(1);
    const [crrProductPage, setCrrProductPage] = React.useState(1);
    const [isUpdated, setIsUpdated] = React.useState(false);
    const [showEmailPopup, setShowEmailPopup] = useState(false);
    const [selectedBuyerEmail, setSelectedBuyerEmail] = useState(null);
    const [isBookmarked, setIsBookmarked] = React.useState(false);
    const [initialLoading, setInitialLoading] = React.useState(true);
    const [actionLoading, setActionLoading] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);

    const { isAdminLogin, isTabletPro, isTablet, isMobilePro, isMobile, isLogin, user, admin } = useContext(AuthContext);

    React.useEffect(() => {
        setInitialLoading(true);
        axios.get(`${hostNameBack}/api/v1/sellers/profile/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
            .then(response => {
                if (response.data.success) {
                    setSeller(response.data.seller);
                    setIsBookmarked(response.data.isBookmarked);
                }
            })
            .catch(e => {
                enqueueSnackbar("Something went wrong!", { variant: "error" });
            }).finally(() => setInitialLoading(false));
    }, [id, isAdminLogin, isUpdated]);

    const handleSendEmail = (user) => {
        setSelectedBuyerEmail(user.email);
        setShowEmailPopup(true);
    };

    React.useEffect(() => {
        axios.get(`${hostNameBack}/api/v1/services/profile/myServices/${id}`, {
            params: { page: crrServicePage, isAdminLogin, isTabletPro, isTablet, isMobilePro, isMobile },
        })
            .then(response => {
                if (response.data.success) {
                    setServices(response.data.allServices);
                    setTotalServicePages(response.data.totalPages);
                }
            });
    }, [id, isAdminLogin, crrServicePage, isTabletPro, isTablet, isMobilePro, isMobile]);

    React.useEffect(() => {
        axios.get(`${hostNameBack}/api/v1/products/profile/myProducts/${id}`, {
            params: { page: crrProductPage, isAdminLogin, isTabletPro, isTablet, isMobilePro, isMobile },
        })
            .then(response => {
                if (response.data.success) {
                    setProducts(response.data.allProducts);
                    setTotalProductPages(response.data.totalPages);
                }
            });
    }, [id, isAdminLogin, crrProductPage, isTabletPro, isTablet, isMobilePro, isMobile]);

    const formatJoinedDate = (date) => {
        const d = new Date(date);
        const monthName = d.toLocaleString('default', { month: 'long' });
        return `${monthName}, ${d.getFullYear()}`;
    };

    const handleBlockUser = async (userId, isBlocked) => {
        if (!window.confirm(isBlocked ? "Are you sure you want to unblock this user?" : `You are blocking user... Are you sure you want to block?`))
            return;

        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.put(
                `${hostNameBack}/api/v1/auth/block/`,
                { userId, isBlocked },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setIsUpdated(prev => !prev);
                enqueueSnackbar(isBlocked ? "User has been UnBlocked!" : 'User has been blocked!', { variant: 'success' });
            }
        } catch (e) {
            enqueueSnackbar(e.response?.data?.error || 'Something went wrong!', { variant: 'error' });
        }
    };

    const handleBookmark = async () => {
        try {
            setActionLoading(true);
            const token = localStorage.getItem('token');
            if (isBookmarked) {
                await axios.delete(`${hostNameBack}/api/v1/bookmarks/${seller._id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIsBookmarked(false);
                enqueueSnackbar("Seller removed from bookmarks", { variant: "success" });
            } else {
                await axios.post(`${hostNameBack}/api/v1/bookmarks`, { sellerId: seller._id }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIsBookmarked(true);
                enqueueSnackbar("Seller added to bookmarks", { variant: "success" });
            }
        } catch (error) {
            enqueueSnackbar("Something went wrong!", { variant: "error" });
            console.log(error);
        }
        finally {
            setActionLoading(false);
        }
    };

    const maxDisplay = isMobile ? 1 : isMobilePro ? 2 : isTablet ? 3 : (isTabletPro || isAdminLogin) ? 4 : 5;

    if (initialLoading) return <Loader type="simpleMax" />

    return (
        <div className='profileDiv'>

            {actionLoading && <Loader type="checkmate" />}

            <section className="bg">
                <img src={`${seller?.profileImage}`} alt="Seller Profile" />
            </section>
            <section className="section">
                <div className="profileContent">
                    <div className="upper">
                        <div className="sellerInfo" style={isAdminLogin ? { minWidth: "42%" } : {}}>
                            <div className="row">
                                <span>Name</span>
                                <span className='fw600'>{seller?.displayName}</span>
                            </div>
                            <div className="row"><span>Username</span><span className='fw600'>{seller?.userId?.username}</span></div>
                            {isAdminLogin && <div className="row"><span>Email</span><span className='fw600'>{seller?.userId?.email}</span></div>}
                            {(isAdminLogin && seller?.userId?.phoneNumber) && <div className="row"><span>Phone</span><span className='fw600'>{seller?.userId?.phoneNumber}</span></div>}
                            <div className="row"><span>Rating</span><div className='ratingsDiv'>
                                <FaStar className='starIconFilled' />
                                <span className='fw600'>{`${seller?.rating?.toFixed(1)} (${seller?.noOfReviews})`}</span>
                            </div></div>
                            <div className="horizontalLine"></div>
                            <div className="row"><span>Country</span><span className='fw600'>{seller?.country}</span></div>
                            <div className="row"><span>Joined</span><span className='fw600'>{formatJoinedDate(seller?.createdAt)}</span></div>
                            <div className="horizontalLine"></div>
                            <div className="row"><span>Languages</span><span className='fw600'>{seller?.languages.join(", ")}</span></div>
                            <div className='buttonsDiv'>
                                {isAdminLogin ? <div className='btnsForAdmin' >
                                    <Link className="primaryBtn" onClick={()=> setIsOpen(!isOpen)}>Contact Seller</Link>
                                    <div className='profileBtns'>
                                        <button onClick={() => handleSendEmail(seller?.userId)} className="secondaryBtn">Send Email</button>
                                        <button onClick={() => handleBlockUser(seller?.userId?._id, seller?.userId?.userStatus === "Blocked")} className="secondaryBtn">{seller?.userId?.userStatus === "Blocked" ? "UnBlock Seller" : "Block Seller"}</button>
                                    </div>
                                    <GenerateAccountStatement sellerId={seller?._id} />
                                </div>
                                    : <div className='btnsForUser'>
                                        <Link className="primaryBtn" to={user?._id === seller?.userId?._id && `/chat?p=${seller?.userId?._id}`} onClick={()=> setIsOpen(!isOpen)}>Contact Me</Link>
                                        {isLogin && user?._id !== seller?.userId?._id && (
                                            <button className='secondaryBtn bookmarkBtn' onClick={handleBookmark}>{isBookmarked ? <FaBookmark /> : <FaRegBookmark />} {isBookmarked ? "Remove" : "Add"}</button>
                                        )}
                                    </div>}
                            </div>
                        </div>
                        <div className="sellerProfileRight">
                            <div className="aboutSeller">
                                <h2 className="secondaryHeading">About <span>Me</span></h2>
                                <div className="horizontalLine"></div>
                                <p>{seller?.description}</p>
                            </div>
                            <AvailabilityStatus seller={seller} />
                        </div>
                    </div>

                    <div className="sellerServices">
                        <h2 className="secondaryHeading">My <span>Services</span></h2>
                        <div className={`services grid-${maxDisplay}`}>
                            {services && services.map((service, index) => {
                                return <ServiceCard key={index} item={service} />
                            })}
                        </div>
                        <Pagination pages={totalServicePages} crrPage={crrServicePage} setCrrPage={setCrrServicePage} />
                    </div>

                    <div className="sellerProducts">
                        <h2 className="secondaryHeading">My <span>Products</span></h2>
                        <div className={`products grid-${maxDisplay}`}>
                            {products && products.map((product, index) => {
                                return <ProductCard key={index} item={product} />
                            })}
                        </div>
                        <Pagination pages={totalProductPages} crrPage={crrProductPage} setCrrPage={setCrrProductPage} />
                    </div>

                    <div className="sellerReviews">
                        <h2 className="primaryHeading">Reviews</h2>
                        <div className="horizontalLine"></div>
                        <Reviews type="seller" id={id} />
                    </div>
                </div>
            </section>

            <AdminSendEmailPopup
                show={showEmailPopup}
                onClose={() => setShowEmailPopup(false)}
                defaultRecipients={selectedBuyerEmail ? [selectedBuyerEmail] : []}
                showButtons={true}
            />

            {(user || admin) && (user?._id || admin?._id) !== seller?.userId?._id && <ChatPopup seller={seller} isOpen={isOpen} setIsOpen={setIsOpen} />}
        </div>
    );
}

export default Profile;